/**
 * FilterSheet Component
 *
 * A bottom sheet for filtering routes by grade, wall section, and completion status.
 *
 * Features:
 * - Grade histogram showing route distribution
 * - Dual-handle range slider for min/max grade
 * - Zone (wall section) chip selection
 * - Hide completed toggle
 * - Apply and reset buttons
 *
 * @example
 * ```tsx
 * <FilterSheet
 *   routes={routes}
 *   wallSections={['Main Wall', 'Side Wall', 'Cave']}
 *   filters={filters}
 *   onFilterChange={setFilters}
 *   onClose={() => setShowFilters(false)}
 * />
 * ```
 */

import { useState, useEffect, useMemo, type CSSProperties } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import type { Route } from '@flash/shared';
import { Toggle } from '@/components/ui/Toggle';

/**
 * Filter state for routes
 */
export interface RouteFilters {
  /** Minimum grade index (0-17 for V0-V17) */
  minGrade: number;
  /** Maximum grade index (0-17 for V0-V17) */
  maxGrade: number;
  /** Selected wall sections (empty = all) */
  wallSections: string[];
  /** Whether to hide completed routes */
  hideCompleted: boolean;
}

/**
 * Default filter state
 */
export const DEFAULT_FILTERS: RouteFilters = {
  minGrade: 0,
  maxGrade: 17,
  wallSections: [],
  hideCompleted: false,
};

/**
 * Props for FilterSheet component
 */
export interface FilterSheetProps {
  /** All routes at the gym (for histogram) */
  routes: Route[];
  /** Available wall sections */
  wallSections: string[];
  /** Current filter state */
  filters: RouteFilters;
  /** Called when filters change */
  onFilterChange: (filters: RouteFilters) => void;
  /** Called when sheet is closed */
  onClose: () => void;
}

/**
 * V-grades for labels
 */
const V_GRADES = [
  'V0',
  'V1',
  'V2',
  'V3',
  'V4',
  'V5',
  'V6',
  'V7',
  'V8',
  'V9',
  'V10',
  'V11',
  'V12',
  'V13',
  'V14',
  'V15',
  'V16',
  'V17',
];

/**
 * Get grade index from V-grade string
 */
const getGradeIndex = (grade: string): number => {
  const match = grade.match(/V(\d+)/);
  const num = match?.[1];
  return num ? parseInt(num, 10) : 0;
};

/**
 * FilterSheet Component
 */
export const FilterSheet = ({
  routes,
  wallSections,
  filters,
  onFilterChange,
  onClose,
}: FilterSheetProps) => {
  const [localFilters, setLocalFilters] = useState<RouteFilters>(filters);
  const [isVisible, setIsVisible] = useState(false);

  // Animate in on mount
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  // Calculate grade distribution for histogram
  const gradeDistribution = useMemo(() => {
    const distribution = new Array(18).fill(0) as number[];
    routes.forEach((route) => {
      if (!route.officialGrade) return; // Skip routes without grades
      const idx = getGradeIndex(route.officialGrade);
      if (idx >= 0 && idx < 18) {
        const current = distribution[idx];
        if (current !== undefined) {
          distribution[idx] = current + 1;
        }
      }
    });
    return distribution;
  }, [routes]);

  // Find max count for histogram scaling
  const maxCount = Math.max(...gradeDistribution, 1);

  // Calculate filtered route count
  const filteredCount = useMemo(() => {
    return routes.filter((route) => {
      // Only apply grade filter if route has an officialGrade
      // Routes without officialGrade (color-only gyms) pass the grade filter
      if (route.officialGrade) {
        const gradeIdx = getGradeIndex(route.officialGrade);
        if (gradeIdx < localFilters.minGrade || gradeIdx > localFilters.maxGrade) return false;
      }
      if (
        localFilters.wallSections.length > 0 &&
        !localFilters.wallSections.includes(route.wallSection)
      )
        return false;
      return true;
    }).length;
  }, [routes, localFilters]);

  // Handle grade range change
  const handleMinGradeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setLocalFilters((prev) => ({
      ...prev,
      minGrade: Math.min(value, prev.maxGrade),
    }));
  };

  const handleMaxGradeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setLocalFilters((prev) => ({
      ...prev,
      maxGrade: Math.max(value, prev.minGrade),
    }));
  };

  // Toggle wall section selection
  const toggleWallSection = (section: string) => {
    setLocalFilters((prev) => {
      const selected = prev.wallSections.includes(section)
        ? prev.wallSections.filter((s) => s !== section)
        : [...prev.wallSections, section];
      return { ...prev, wallSections: selected };
    });
  };

  // Reset filters
  const handleReset = () => {
    setLocalFilters(DEFAULT_FILTERS);
  };

  // Apply filters
  const handleApply = () => {
    onFilterChange(localFilters);
    handleClose();
  };

  // Close with animation
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          ...backdropStyle,
          opacity: isVisible ? 1 : 0,
        }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        style={{
          ...sheetStyle,
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Filter routes"
      >
        {/* Header */}
        <header style={headerStyle}>
          <button onClick={handleClose} style={headerButtonStyle} aria-label="Close filters">
            <CloseIcon />
          </button>
          <h2 style={headerTitleStyle}>Filters</h2>
          <button onClick={handleReset} style={resetButtonStyle}>
            Reset
          </button>
        </header>

        {/* Content */}
        <div style={contentStyle}>
          {/* Route Count */}
          <p style={routeCountStyle}>
            {filteredCount} of {routes.length} routes
          </p>

          {/* Grade Section */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Grade Range</h3>

            {/* Histogram */}
            <div style={histogramContainerStyle}>
              {gradeDistribution.map((count, idx) => {
                const isInRange = idx >= localFilters.minGrade && idx <= localFilters.maxGrade;
                const height = (count / maxCount) * 60;
                return (
                  <div key={idx} style={histogramBarContainerStyle}>
                    <div
                      style={{
                        ...histogramBarStyle,
                        height: `${Math.max(height, 2)}px`,
                        backgroundColor: isInRange
                          ? 'var(--color-primary, #3b82f6)'
                          : 'var(--color-border, #e5e7eb)',
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Grade Labels */}
            <div style={gradeLabelsStyle}>
              <span>{V_GRADES[localFilters.minGrade]}</span>
              <span>{V_GRADES[localFilters.maxGrade]}</span>
            </div>

            {/* Range Sliders */}
            <div style={sliderContainerStyle}>
              {/* Style tag for slider thumb pointer-events */}
              <style>{`
                .filter-range-slider {
                  pointer-events: none;
                }
                .filter-range-slider::-webkit-slider-thumb {
                  pointer-events: auto;
                }
                .filter-range-slider::-moz-range-thumb {
                  pointer-events: auto;
                }
              `}</style>
              <input
                type="range"
                min="0"
                max="17"
                value={localFilters.minGrade}
                onChange={handleMinGradeChange}
                style={sliderStyle}
                className="filter-range-slider"
                aria-label="Minimum grade"
              />
              <input
                type="range"
                min="0"
                max="17"
                value={localFilters.maxGrade}
                onChange={handleMaxGradeChange}
                style={sliderStyle}
                className="filter-range-slider"
                aria-label="Maximum grade"
              />
            </div>
          </section>

          {/* Regions Section */}
          {wallSections.length > 0 && (
            <section style={sectionStyle}>
              <h3 style={sectionTitleStyle}>Regions</h3>
              <div style={zoneChipsStyle}>
                {wallSections.map((section) => {
                  const isSelected =
                    localFilters.wallSections.length === 0 ||
                    localFilters.wallSections.includes(section);
                  return (
                    <button
                      key={section}
                      onClick={() => toggleWallSection(section)}
                      style={{
                        ...zoneChipStyle,
                        ...(isSelected ? zoneChipSelectedStyle : {}),
                      }}
                      aria-pressed={localFilters.wallSections.includes(section)}
                    >
                      {section}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Hide Completed Section */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>My Profile</h3>
            <div style={toggleRowStyle}>
              <span style={toggleRowLabelStyle}>Hide completed</span>
              <Toggle
                checked={localFilters.hideCompleted}
                onChange={(checked) =>
                  setLocalFilters((prev) => ({ ...prev, hideCompleted: checked }))
                }
                aria-label="Hide completed routes"
              />
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer style={footerStyle}>
          <button onClick={handleApply} style={applyButtonStyle}>
            Apply
          </button>
        </footer>
      </div>
    </>
  );
};

// Styles
const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  transition: 'opacity 300ms ease-out',
  zIndex: 999,
};

const sheetStyle: CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  maxHeight: '85vh',
  backgroundColor: 'var(--color-bg-primary, white)',
  borderTopLeftRadius: '16px',
  borderTopRightRadius: '16px',
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
  transition: 'transform 300ms ease-out',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid var(--color-border, #e5e7eb)',
};

const headerButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  padding: 0,
  border: 'none',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
};

const headerTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const resetButtonStyle: CSSProperties = {
  padding: '8px 12px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
};

const contentStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '20px',
};

const routeCountStyle: CSSProperties = {
  margin: '0 0 20px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
  textAlign: 'center',
};

const sectionStyle: CSSProperties = {
  marginBottom: '24px',
};

const sectionTitleStyle: CSSProperties = {
  margin: '0 0 12px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const histogramContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  height: '60px',
  gap: '2px',
  marginBottom: '8px',
};

const histogramBarContainerStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};

const histogramBarStyle: CSSProperties = {
  width: '100%',
  borderRadius: '2px 2px 0 0',
  transition: 'background-color 200ms ease-out',
};

const gradeLabelsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
  marginBottom: '8px',
};

const sliderContainerStyle: CSSProperties = {
  position: 'relative',
  height: '24px',
};

const sliderStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  WebkitAppearance: 'none',
  appearance: 'none',
  background: 'transparent',
  cursor: 'pointer',
};

const zoneChipsStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
};

const zoneChipStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '20px',
  cursor: 'pointer',
  transition: 'all 200ms ease-out',
};

const zoneChipSelectedStyle: CSSProperties = {
  color: 'var(--color-primary, #3b82f6)',
  backgroundColor: 'rgba(59, 130, 246, 0.1)',
  borderColor: 'var(--color-primary, #3b82f6)',
};

const toggleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 0',
};

const toggleRowLabelStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary, #1f2937)',
};

const footerStyle: CSSProperties = {
  padding: '16px 20px',
  paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
  borderTop: '1px solid var(--color-border, #e5e7eb)',
};

const applyButtonStyle: CSSProperties = {
  width: '100%',
  padding: '16px',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
};

export default FilterSheet;
